import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from "../components/ui/dialog";

import Navbar from '../components/ui/Navbar';
import { 
  Clock, Ban, MapPin, Eye, Calendar, ChevronLeft, ChevronRight,
  Star, ShieldCheck, CheckCircle2, AlertTriangle, Home, Hash, MessageSquare, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

import planoFundo from "../assets/planodefundo.png"; 

const History = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  // --- PAGINAÇÃO (20 por página) ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // --- ESTADOS DOS MODAIS ---
  const [viewOrder, setViewOrder] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRating, setCancelRating] = useState(0); 
  
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [orderToRate, setOrderToRate] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  const translateStatus = (status) => {
    const statusMap = {
      'open': 'Em Aberto',
      'waiting_approval': 'Análise',
      'accepted': 'Aceito',
      'in_progress': 'Em Andamento',
      'completed': 'Finalizado',
      'cancelled': 'Cancelado'
    };
    return statusMap[status] || status;
  };

  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const q = query(collection(db, 'orders'), where('clientId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = ordersData.sort((a, b) => {
        const da = a.createdAt?.seconds ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const db_ = b.createdAt?.seconds ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return db_ - da;
      });
      setOrders(sorted);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [user]);

  const formatDateTime = (createdAt) => {
    if (!createdAt) return "---";
    let date = createdAt.seconds ? createdAt.toDate() : new Date(createdAt);
    return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // --- FILTRO + PAGINAÇÃO ---
  const filteredOrders = orders.filter(o => 
    filter === 'all' || o.status === filter || (filter === 'in_progress' && (o.status === 'accepted' || o.status === 'waiting_approval'))
  );

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const currentOrders = filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // --- LÓGICA DE CANCELAMENTO COM AVALIAÇÃO ---
  const handleCancelOrder = async () => {
    const order = orders.find(o => o.id === orderToCancel);
    const needsRating = order?.technicianId && (order.status === 'accepted' || order.status === 'in_progress' || order.status === 'waiting_approval');

    if (!cancelReason.trim()) return toast.error("Informe o motivo do cancelamento.");
    if (needsRating && cancelRating === 0) return toast.error("Por favor, avalie a experiência antes de cancelar.");

    try {
      const updateData = { 
        status: 'cancelled', 
        cancellationReason: cancelReason, 
        updatedAt: new Date().toISOString() 
      };

      if (needsRating) {
        updateData.rating = cancelRating;
        updateData.ratingComment = `[CANCELADO PELO CLIENTE]: ${cancelReason}`;
        updateData.ratedAt = new Date().toISOString();
      }

      await updateDoc(doc(db, 'orders', orderToCancel), updateData);
      toast.success("Pedido cancelado com sucesso.");
      setShowCancelDialog(false);
      setCancelReason('');
      setCancelRating(0);
    } catch (e) { toast.error("Erro ao cancelar."); }
  };

  const handleSendRating = async () => {
    if (ratingValue === 0) return toast.error("Selecione uma nota.");
    try {
      await updateDoc(doc(db, 'orders', orderToRate), {
        rating: ratingValue,
        ratingComment: ratingComment,
        ratedAt: new Date().toISOString()
      });
      toast.success("Avaliação enviada!");
      setShowRatingDialog(false);
      setRatingValue(0);
      setRatingComment('');
    } catch (e) { toast.error("Erro ao avaliar."); }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-blue-600" /></div>;

  return (
    <div className="relative min-h-screen font-sans text-slate-900 overflow-x-hidden text-left bg-slate-50/50">
      <div className="fixed inset-0 -z-10 w-full h-full opacity-[0.05]" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover' }}></div>
      <Navbar />

      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-32 pb-20">
        <div className="mb-10 px-4 text-left">
            <h1 className="text-4xl font-black uppercase italic tracking-tighter text-slate-900 text-left">MEU <span className="text-blue-600">HISTÓRICO</span></h1>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-2 italic text-left">Página {currentPage} de {totalPages || 1}</p>
        </div>

        {/* FILTROS */}
        <div className="flex flex-wrap gap-2 mb-10 bg-white/90 backdrop-blur-md p-2 rounded-[2.5rem] shadow-xl border border-white/50 overflow-hidden">
          {['all', 'open', 'in_progress', 'completed', 'cancelled'].map((f) => (
            <Button key={f} onClick={() => {setFilter(f); setCurrentPage(1);}} variant="ghost" className={`flex-1 min-w-[100px] rounded-2xl font-black text-[9px] uppercase h-12 transition-all ${filter === f ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400'}`}>
              {f === 'all' ? 'Todos' : translateStatus(f)}
            </Button>
          ))}
        </div>

        <div className="space-y-6">
          {currentOrders.length > 0 ? currentOrders.map((order) => {
            const isCompleted = order.status === 'completed';
            const isCancelled = order.status === 'cancelled';
            
            return (
              <Card key={order.id} className={`bg-white rounded-[2.5rem] border-none shadow-xl transition-all border-l-[10px] overflow-hidden ${isCompleted ? 'border-l-green-500' : isCancelled ? 'border-l-red-500' : 'border-l-blue-600'}`}>
                <CardContent className="p-8">
                  <div className="flex flex-col lg:flex-row justify-between gap-6 items-start lg:items-center text-left">
                    <div className="flex-1 space-y-4 text-left">
                      <div className="flex items-center gap-3">
                        <Badge className={`px-4 py-1 rounded-full font-black text-[9px] uppercase border-none shadow-sm ${isCompleted ? 'bg-green-500 text-white' : isCancelled ? 'bg-red-500 text-white' : 'bg-blue-600 text-white'}`}>
                          {translateStatus(order.status)}
                        </Badge>
                        <span className="text-[10px] font-black text-slate-300 uppercase italic tracking-widest">ID: {order.serviceCode}</span>
                      </div>
                      <h3 className="font-black text-2xl text-slate-900 uppercase italic tracking-tighter text-left">{order.serviceArea}</h3>
                      <div className="flex items-center gap-2 bg-blue-50/50 w-fit px-4 py-2 rounded-xl text-blue-700 font-black text-[10px] uppercase italic">
                         <Calendar className="w-3.5 h-3.5" /> {formatDateTime(order.createdAt)}
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                      <Button onClick={() => setViewOrder(order)} className="h-14 px-8 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-[10px] italic shadow-lg flex items-center gap-2">
                        <Eye size={16} /> DETALHES DO PEDIDO
                      </Button>
                      {isCompleted && !order.rating && (
                        <Button onClick={() => {setOrderToRate(order.id); setShowRatingDialog(true);}} className="h-14 px-8 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-black uppercase text-[10px] italic shadow-lg flex items-center gap-2"><Star size={16} className="fill-white" /> AVALIAR</Button>
                      )}
                      {!isCompleted && !isCancelled && (
                        <Button onClick={() => {setOrderToCancel(order.id); setShowCancelDialog(true);}} variant="ghost" className="h-14 px-6 rounded-2xl text-red-500 font-black hover:bg-red-50 text-[10px] uppercase italic gap-2"><Ban size={16} /> CANCELAR</Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }) : (
            <div className="text-center py-20 font-black uppercase text-slate-300 italic tracking-widest text-left">Nenhum pedido encontrado.</div>
          )}
        </div>

        {/* PAGINAÇÃO */}
        {totalPages > 1 && (
          <div className="mt-12 flex justify-center items-center gap-6">
             <Button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="w-12 h-12 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30"><ChevronLeft size={20} /></Button>
             <span className="font-black text-xs text-slate-900 uppercase italic tracking-tighter">Página {currentPage} / {totalPages}</span>
             <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="w-12 h-12 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30"><ChevronRight size={20} /></Button>
          </div>
        )}
      </main>

      {/* --- SUB-JANELA DE DETALHES (MODAL) --- */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className="max-w-2xl rounded-[3rem] p-0 border-none bg-slate-950 text-white overflow-hidden shadow-2xl z-[500]">
          {viewOrder && (
            <div className="relative text-left">
              <div className="p-8 md:p-10 border-b border-white/5 bg-gradient-to-b from-white/5 to-transparent text-left">
                <div className="flex justify-between items-start mb-6">
                  <div className="text-left">
                    <Badge className="bg-blue-600 text-white font-black text-[9px] uppercase italic mb-2 tracking-widest">{translateStatus(viewOrder.status)}</Badge>
                    <h2 className="text-3xl font-black uppercase italic tracking-tighter leading-none text-left">{viewOrder.serviceArea}</h2>
                  </div>
                  <div className="text-right">
                    <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Protocolo</p>
                    <p className="text-xl font-black text-orange-500 uppercase italic">#{viewOrder.serviceCode}</p>
                  </div>
                </div>
              </div>

              <div className="p-8 md:p-10 space-y-8 text-left">
                {/* EXIBIÇÃO DA AVALIAÇÃO SE EXISTIR */}
                {viewOrder.rating > 0 && (
                  <div className="p-6 bg-orange-500/10 rounded-[2rem] border border-orange-500/20 text-left">
                    <div className="flex items-center gap-2 mb-2">
                       <Label className="text-[10px] font-black uppercase text-orange-400 italic">Sua Avaliação:</Label>
                       <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => <Star key={i} size={12} className={`${i < viewOrder.rating ? 'fill-orange-500 text-orange-500' : 'text-slate-800'}`} />)}
                       </div>
                    </div>
                    <p className="text-slate-200 italic font-bold text-sm">"{viewOrder.ratingComment || 'Avaliado sem comentário.'}"</p>
                  </div>
                )}

                <div>
                  <Label className="text-[10px] font-black text-blue-400 uppercase italic mb-3 block tracking-widest text-left">Descrição</Label>
                  <p className="text-slate-300 italic text-sm bg-white/5 p-6 rounded-3xl border border-white/5">"{viewOrder.description}"</p>
                </div>

                <div className="flex items-start gap-4 text-left">
                  <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center shrink-0 border border-red-500/20"><MapPin size={24} className="text-red-500" /></div>
                  <div className="text-left flex-1 text-left">
                    <Label className="text-[9px] font-black text-slate-500 uppercase italic mb-1 block text-left">Endereço</Label>
                    <p className="text-base font-bold uppercase italic leading-tight text-left">{viewOrder.address}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch text-left">
                  <div className="p-6 bg-white/5 rounded-[2rem] border border-white/10 flex flex-col justify-center text-left">
                    <Label className="text-[9px] font-black text-orange-400 uppercase italic mb-4 block tracking-widest text-left">Técnico</Label>
                    {viewOrder.technicianId ? (
                      <div className="flex items-center gap-4 w-full text-left">
                        <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center text-xl font-black italic shrink-0 uppercase shadow-lg">{viewOrder.technicianName?.charAt(0)}</div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-base font-black uppercase italic leading-none truncate break-words text-left">{viewOrder.technicianName}</p>
                          <div className="flex items-center gap-2 mt-2">
                             <Badge className="bg-green-500 text-white font-black text-[7px] uppercase px-2 border-none">Verificado</Badge>
                             {viewOrder.status !== 'completed' && viewOrder.status !== 'cancelled' && (
                               <button onClick={() => {setViewOrder(null); navigate(`/chat/${viewOrder.technicianId}`);}} className="text-[9px] font-black text-blue-400 hover:text-white flex items-center gap-1 italic"><MessageSquare size={12}/> CHAT</button>
                             )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-500 italic text-[10px] font-black uppercase tracking-widest text-center">AGUARDANDO...</p>
                    )}
                  </div>

                  {viewOrder.status !== 'completed' && viewOrder.status !== 'cancelled' && (
                    <div className="p-6 bg-blue-600 rounded-[2rem] flex flex-col items-center justify-center text-center shadow-xl border-2 border-white/10 min-h-[120px]">
                      <p className="text-[10px] font-black text-blue-100 uppercase tracking-widest mb-2 flex items-center gap-2"><ShieldCheck size={14}/> SEGURANÇA</p>
                      <span className="text-4xl font-black tracking-[0.3em] text-white drop-shadow-lg">{viewOrder.serviceCode}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 flex justify-center bg-slate-900/50">
                <Button onClick={() => setViewOrder(null)} variant="ghost" className="text-slate-500 font-black uppercase italic text-[10px] tracking-widest hover:text-white">FECHAR</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- MODAL CANCELAR (COM AVALIAÇÃO OBRIGATÓRIA) --- */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-white rounded-[3rem] p-10 text-left z-[600]">
          <DialogHeader className="text-left">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6 shadow-inner"><AlertTriangle className="text-red-600" /></div>
            <DialogTitle className="text-2xl font-black uppercase italic text-slate-900 text-left">CANCELAR PEDIDO?</DialogTitle>
          </DialogHeader>
          <div className="my-6 space-y-6 text-left">
            <div className="space-y-2 text-left">
              <Label className="text-[10px] font-black uppercase text-slate-400 italic block text-left">Justificativa</Label>
              <Input placeholder="Escreva o motivo..." className="h-14 rounded-2xl font-bold bg-slate-50 border-red-50" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
            </div>

            {/* AVALIAÇÃO NO CANCELAMENTO SE JÁ TIVER TÉCNICO */}
            {orders.find(o => o.id === orderToCancel)?.technicianId && (
              <div className="p-5 bg-orange-50/50 rounded-2xl border border-orange-100 text-center">
                 <p className="text-[10px] font-black text-orange-600 uppercase mb-3 italic">Como foi a experiência até aqui?</p>
                 <div className="flex justify-center gap-2">
                    {[1,2,3,4,5].map(s => <Star key={s} onClick={() => setCancelRating(s)} className={`w-8 h-8 cursor-pointer transition-all ${cancelRating >= s ? 'fill-orange-500 text-orange-500' : 'text-slate-200'}`} />)}
                 </div>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={handleCancelOrder} className="w-full h-16 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black uppercase italic text-xs shadow-xl">CONFIRMAR CANCELAMENTO</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- MODAL AVALIAR (FLUXO NORMAL) --- */}
      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent className="bg-white rounded-[3rem] p-10 text-left z-[600]">
          <DialogHeader className="text-left"><DialogTitle className="text-2xl font-black uppercase italic text-slate-900 text-left">AVALIAR TÉCNICO</DialogTitle></DialogHeader>
          <div className="flex justify-center gap-3 my-8">
            {[1,2,3,4,5].map(s => <Star key={s} onClick={() => setRatingValue(s)} className={`w-10 h-10 cursor-pointer transition-all active:scale-110 ${ratingValue >= s ? 'fill-orange-500 text-orange-500 scale-110' : 'text-slate-200'}`} />)}
          </div>
          <Textarea placeholder="Descreva como foi o atendimento..." className="rounded-2xl bg-slate-50 min-h-[120px] mb-6 p-5 font-bold" value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} />
          <Button onClick={handleSendRating} className="w-full h-16 bg-orange-500 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl">ENVIAR AVALIAÇÃO</Button>
        </DialogContent>
      </Dialog>

      <style dangerouslySetInnerHTML={{ __html: `.no-scrollbar::-webkit-scrollbar { display: none; }` }} />
    </div>
  );
};

export default History;